/** Shared Recharts axis labels for production reports (UDOMI-909, UDOMI-950). */
export const WEEKLY_FEET_PER_DAY_X_AXIS_LABEL = 'Day of Week';
export const WEEKLY_FEET_PER_DAY_Y_AXIS_LABEL = 'Feet Processed at Each Stage';

export const weeklyFeetPerDayBarChartMargin = {
  top: 20,
  right: 30,
  left: 148,
  // Reduced bottom: legend moved outside BarChart (UDOMI-1034)
  bottom: 40,
} as const;

export const weeklyFeetPerDayYAxisWidth = 88;

/** Align stage legend with the Recharts plot band (matches X-axis label center). */
export const weeklyFeetPerDayPlotBandSx = {
  marginLeft: `${weeklyFeetPerDayBarChartMargin.left + weeklyFeetPerDayYAxisWidth}px`,
  width: `calc(100% - ${
    weeklyFeetPerDayBarChartMargin.left +
    weeklyFeetPerDayYAxisWidth +
    weeklyFeetPerDayBarChartMargin.right
  }px)`,
} as const;

const CATEGORY_AXIS_MIN_WIDTH = 200;
const CATEGORY_AXIS_MAX_WIDTH = 480;
/** Conservative px/char so long labels truncate before invading the plot (UDOMI-951). */
const CATEGORY_AXIS_CHAR_WIDTH = 10;

/** Dynamic Y-axis width for vertical bar charts with long category labels (UDOMI-951). */
export function getCategoryAxisWidth(labels: string[]): number {
  if (labels.length === 0) {
    return CATEGORY_AXIS_MIN_WIDTH;
  }
  const longest = Math.max(...labels.map(label => label.length));
  return Math.min(
    CATEGORY_AXIS_MAX_WIDTH,
    Math.max(CATEGORY_AXIS_MIN_WIDTH, longest * CATEGORY_AXIS_CHAR_WIDTH)
  );
}

/** Max characters that fit in the category axis at the given pixel width. */
export function getCategoryTickMaxLength(axisWidth: number): number {
  return Math.max(12, Math.floor(axisWidth / CATEGORY_AXIS_CHAR_WIDTH) - 1);
}

/** Truncate long category labels for chart axis ticks; full text remains in tooltip. */
export function truncateCategoryLabel(label: string, maxLength: number): string {
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, Math.max(0, maxLength - 1))}…`;
}

/** Margin for vertical product bar charts (UDOMI-951). Y-axis width is set separately on YAxis. */
export function getProductsReportBarChartMargin(categoryAxisWidth = CATEGORY_AXIS_MIN_WIDTH) {
  const leftMargin = categoryAxisWidth > 280 ? 24 : 16;
  return { top: 5, right: 30, left: leftMargin, bottom: 28 };
}

/** Format products-report size feet + inches for chart/table (UDOMI-1046). */
export function formatProductsReportSize(feet: number, inches: number | null | undefined): string {
  const inchVal = inches ?? 0;
  if (inchVal === 0) {
    return `${feet}'`;
  }
  // Trim trailing zeros for half-inch values (e.g. 9.5)
  const inchText = Number.isInteger(inchVal) ? String(inchVal) : String(inchVal);
  return `${feet}' ${inchText}"`;
}
